import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, ShowerHead, Check, AlertTriangle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

export default function DocceTab({ area, setArea }) {
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [giornata, setGiornata] = useState(null);
  const [piano, setPiano] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nuova, setNuova] = useState({ ospite_id: '', giorno: 'lun', settimana: '1' });

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const [g, p, a] = await Promise.all([
        api.get('/moduli/docce', { params: { data, area } }),
        api.get('/moduli/docce/piano', { params: { area } }),
        api.get('/moduli/docce/alert', { params: { giorni: 7, area } }),
      ]);
      setGiornata(g.data); setPiano(p.data); setAlert(a.data);
    } catch { toast.error('Errore caricamento piano docce'); }
    setLoading(false);
  }, [data, area]);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const esegui = async (ospiteId, fatta) => {
    try {
      if (fatta) await api.delete('/moduli/docce/esegui', { params: { ospite_id: ospiteId, data } });
      else await api.post('/moduli/docce/esegui', { ospite_id: ospiteId, data });
      fetchDati();
    } catch { toast.error('Errore registrazione doccia'); }
  };

  const aggiungiPiano = async () => {
    if (!nuova.ospite_id) { toast.error('Seleziona un ospite'); return; }
    try {
      await api.post('/moduli/docce/piano', { ...nuova, settimana: parseInt(nuova.settimana), area });
      toast.success('Ospite inserito nel piano docce');
      setNuova(n => ({ ...n, ospite_id: '' }));
      fetchDati();
    } catch { toast.error('Errore inserimento nel piano'); }
  };

  const rimuoviPiano = async (voceId) => {
    try { await api.delete(`/moduli/docce/piano/${voceId}`); fetchDati(); }
    catch { toast.error('Errore rimozione'); }
  };

  const stampa = () => {
    if (!piano) return;
    let html = '';
    [1, 2].forEach(sett => {
      html += `<div class="section-title">${sett === 1 ? 'Prima' : 'Seconda'} settimana</div><table><thead><tr>${piano.giorni.map(g => `<th>${piano.giorni_label[g]}</th>`).join('')}</tr></thead><tbody><tr>`;
      piano.giorni.forEach(g => {
        const voci = piano.piano[String(sett)][g] || [];
        html += `<td style="vertical-align:top">${voci.map(v => `${v.ospite.cognome} ${v.ospite.nome}<br/>`).join('') || '-'}</td>`;
      });
      html += '</tr></tbody></table>';
    });
    openPrintWindow('Piano Docce', html, { subtitle: `Area ${area || 'tutte'}` });
  };

  if (loading && !giornata) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="docce-tab">
      <div className="flex flex-wrap items-center gap-2">
        <DatePickerField value={data} onChange={v => setData(v)} className="w-40 h-9" testId="docce-data" />
        <Select value={area || 'tutte'} onValueChange={v => setArea(v === 'tutte' ? '' : v)}>
          <SelectTrigger className="w-40 h-9" data-testid="docce-area"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le aree</SelectItem>
            <SelectItem value="verde">Area Verde</SelectItem>
            <SelectItem value="gialla">Area Gialla</SelectItem>
          </SelectContent>
        </Select>
        <Badge className="bg-brand-soft text-brand-dark">{giornata?.settimana === 2 ? 'Seconda' : 'Prima'} settimana</Badge>
        <Button size="sm" variant="outline" onClick={stampa} className="h-9" data-testid="docce-print">
          <Printer className="w-4 h-4 mr-1" />Stampa piano
        </Button>
      </div>

      {alert?.totale > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="docce-alert">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800">
              <strong>{alert.totale} ospiti</strong> del piano senza doccia registrata negli ultimi 7 giorni:{' '}
              {alert.ospiti.slice(0, 10).map(o => `${o.cognome} ${o.nome}`).join(' · ')}
              {alert.ospiti.length > 10 ? ` e altri ${alert.ospiti.length - 10}` : ''}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="docce-oggi">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ShowerHead className="w-4 h-4 text-brand" />
            Docce previste il {new Date(data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            <Badge variant="outline" className="ml-1 text-[10px]">{giornata?.eseguite || 0} eseguite</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(giornata?.previsti || []).length === 0 && (
            <p className="text-xs text-slate-400">Nessuna doccia programmata per questo giorno.</p>
          )}
          {(giornata?.previsti || []).map(o => (
            <div key={o.id} className="flex items-center justify-between p-2 rounded-lg border border-slate-200" data-testid={`doccia-row-${o.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{o.cognome} {o.nome}</p>
                <p className="text-[11px] text-slate-400">
                  Stanza {o.stanza || '-'} · letto {o.letto || '-'}
                  {o.eseguita ? ` · fatta alle ${o.ora} da ${o.operatore_nome}` : ''}
                </p>
              </div>
              <Button size="sm" onClick={() => esegui(o.id, o.eseguita)}
                className={o.eseguita ? 'h-8 bg-brand text-white' : 'h-8'} variant={o.eseguita ? 'default' : 'outline'}
                data-testid={`doccia-toggle-${o.id}`}>
                {o.eseguita ? <><Check className="w-3.5 h-3.5 mr-1" />Fatta</> : 'Registra doccia'}
              </Button>
            </div>
          ))}
          {(giornata?.fuori_piano || []).map(o => (
            <div key={o.id} className="flex items-center justify-between p-2 rounded-lg border border-dashed border-slate-200 bg-slate-50">
              <div>
                <p className="text-sm font-medium text-slate-700">{o.cognome} {o.nome} <span className="text-[10px] text-slate-400">(fuori piano)</span></p>
                <p className="text-[11px] text-slate-400">fatta alle {o.ora} da {o.operatore_nome}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => esegui(o.id, true)}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card data-testid="docce-piano">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Piano settimanale (2 settimane alternate)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2].map(sett => (
            <div key={sett}>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                {sett === 1 ? 'Prima' : 'Seconda'} settimana
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {piano.giorni.map(g => (
                  <div key={g} className="rounded-lg border border-slate-200 p-2 min-h-[70px]">
                    <p className="text-[10px] font-semibold text-brand-dark uppercase mb-1">{piano.giorni_label[g]}</p>
                    {(piano.piano[String(sett)][g] || []).map(v => (
                      <div key={v.id} className="flex items-center justify-between gap-1 group">
                        <span className="text-[11px] text-slate-600 truncate">{v.ospite.cognome} {v.ospite.nome?.[0]}.</span>
                        <button onClick={() => rimuoviPiano(v.id)} className="opacity-0 group-hover:opacity-100 text-red-400" data-testid={`piano-del-${v.id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
            <div className="space-y-1">
              <p className="text-[10px] text-slate-500">Ospite</p>
              <Select value={nuova.ospite_id} onValueChange={v => setNuova(n => ({ ...n, ospite_id: v }))}>
                <SelectTrigger className="h-9 w-56 text-xs" data-testid="piano-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(piano.ospiti || []).map(o => <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-slate-500">Giorno</p>
              <Select value={nuova.giorno} onValueChange={v => setNuova(n => ({ ...n, giorno: v }))}>
                <SelectTrigger className="h-9 w-36 text-xs" data-testid="piano-giorno"><SelectValue /></SelectTrigger>
                <SelectContent>{piano.giorni.map(g => <SelectItem key={g} value={g}>{piano.giorni_label[g]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-slate-500">Settimana</p>
              <Select value={nuova.settimana} onValueChange={v => setNuova(n => ({ ...n, settimana: v }))}>
                <SelectTrigger className="h-9 w-32 text-xs" data-testid="piano-settimana"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Prima</SelectItem><SelectItem value="2">Seconda</SelectItem></SelectContent>
              </Select>
            </div>
            <Button size="sm" className="h-9 bg-brand hover:bg-brand-dark text-white" onClick={aggiungiPiano} data-testid="piano-add">
              Inserisci nel piano
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
